'use client';

import type React from 'react';

import { Badge } from '@/Components/ui/badge';
import { Button } from '@/Components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/Components/ui/card';
import { Input } from '@/Components/ui/input';
import GuestLayout from '@/Layouts/GuestLayout';
import { router } from '@inertiajs/react';
import {
    Calendar,
    Code2,
    Database,
    FileText,
    Hash,
    ImageIcon,
    Network,
    Search,
    Shield,
    Star,
    Zap,
} from 'lucide-react';
import { useState } from 'react';
import { route } from 'ziggy-js';
import HeroSection from './HeroSection';

interface Tool {
    id: string;
    name: string;
    description: string;
    icon: React.ReactNode;
    category: string;
    isNew?: boolean;
    isPopular?: boolean;
    isHidden?: boolean;
}

const tools: Tool[] = [
    {
        id: 'api-tester',
        name: 'API Debugger',
        description:
            'Generate test endpoints and monitor API calls in real-time with authentication support',
        icon: <Network className="h-6 w-6" />,
        category: 'API & Network',
        isPopular: true,
        isHidden: false,
    },
    {
        id: 'certificate-viewer',
        name: 'Certificate Viewer',
        description:
            'View and validate digital certificates using the Nexus verification widget',
        icon: <Shield className="h-6 w-6" />,
        category: 'Validation',
        isNew: false,
        isHidden: false,
    },
    {
        id: 'product-planning',
        name: 'Product Planning',
        description:
            'Prioritize, score, and schedule work items with custom factors and Gantt-style timeline visualization',
        icon: <Calendar className="h-6 w-6" />,
        category: 'Productivity',
        isNew: true,
        isHidden: false,
    },
    {
        id: 'base64',
        name: 'Base64 Encoder/Decoder',
        description:
            'Encode and decode Base64 strings with support for text, URLs, and binary data',
        icon: <Code2 className="h-6 w-6" />,
        category: 'Encoding',
        isPopular: true,
        isHidden: false,
    },
    {
        id: 'sample-data-generator',
        name: 'Sample Data Generator',
        description:
            'Create arrays of sample objects with custom keys and value types for API responses',
        icon: <Database className="h-6 w-6" />,
        category: 'Data Processing',
        isNew: true,
        isHidden: false,
    },
    {
        id: 'openbadge-validator',
        name: 'OpenBadge Validator',
        description:
            'Validate OpenBadge credentials and verify digital badge authenticity',
        icon: <Shield className="h-6 w-6" />,
        category: 'Validation',
        isHidden: true,
    },
    {
        id: 'png-text-decoder',
        name: 'PNG Text Decoder',
        description:
            'Extract hidden text metadata from PNG images and analyze image properties',
        icon: <ImageIcon className="h-6 w-6" />,
        category: 'Image Processing',
        isNew: true,
        isHidden: true,
    },
    {
        id: 'hash-generator',
        name: 'Hash Generator',
        description:
            'Generate MD5, SHA-1, SHA-256, and other hash values for text and files',
        icon: <Hash className="h-6 w-6" />,
        category: 'Cryptography',
        isHidden: true,
    },
    {
        id: 'json-formatter',
        name: 'JSON Formatter',
        description:
            'Format, validate, and minify JSON data with syntax highlighting',
        icon: <FileText className="h-6 w-6" />,
        category: 'Data Processing',
        isPopular: true,
        isHidden: true,
    },
    {
        id: 'url-encoder',
        name: 'URL Encoder/Decoder',
        description:
            'Encode and decode URLs with support for query parameters and special characters',
        icon: <Code2 className="h-6 w-6" />,
        category: 'Encoding',
        isHidden: true,
    },
    {
        id: 'regex-tester',
        name: 'Regex Tester',
        description:
            'Test regular expressions with real-time matching and explanation',
        icon: <Search className="h-6 w-6" />,
        category: 'Text Processing',
        isHidden: true,
    },
    {
        id: 'jwt-decoder',
        name: 'JWT Decoder',
        description:
            'Decode and verify JSON Web Tokens with header and payload inspection',
        icon: <Shield className="h-6 w-6" />,
        category: 'Security',
        isNew: true,
        isHidden: true,
    },
];

const usedCategories = Array.from(
    new Set(
        tools.filter((tool) => !tool.isHidden).map((tool) => tool.category),
    ),
);
const categories = ['All', ...usedCategories];

export default function HomePage() {
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');

    const filteredTools = tools.filter((tool) => {
        const matchesCategory =
            selectedCategory === 'All' || tool.category === selectedCategory;
        const matchesSearch =
            tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            tool.description.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch && !tool.isHidden;
    });

    return (
        <GuestLayout maxW7xl={true}>
            <HeroSection
                title={<>Debug Tools Hub</>}
                subtitle={
                    <>
                        A comprehensive collection of debugging and development
                        utilities. Test APIs, encode data, validate formats, and
                        troubleshoot issues with instant feedback.
                    </>
                }
                highlights={
                    <>
                        <div className="flex items-center gap-1">
                            <Star className="h-4 w-4" />
                            <span>Free to use</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <Zap className="h-4 w-4" />
                            <span>No registration required</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <Shield className="h-4 w-4" />
                            <span>
                                Completely private with no data retention
                            </span>
                        </div>
                    </>
                }
            />

            {/* Search and Filter */}
            <section className="mb-8 px-4">
                <div className="mx-auto">
                    <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <div className="flex w-full flex-col gap-2 md:flex-row md:items-center md:gap-4">
                            <div className="relative max-w-md flex-1">
                                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
                                <Input
                                    placeholder="Search tools..."
                                    value={searchQuery}
                                    onChange={(e) =>
                                        setSearchQuery(e.target.value)
                                    }
                                    className="pl-10"
                                />
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {categories.map((category) => (
                                    <Button
                                        key={category}
                                        variant={
                                            selectedCategory === category
                                                ? 'default'
                                                : 'outline'
                                        }
                                        size="sm"
                                        onClick={() =>
                                            setSelectedCategory(category)
                                        }
                                    >
                                        {category}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Tools Grid */}
            <section className="px-4 pb-16">
                <div className="mx-auto">
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {filteredTools.map((tool) => (
                            <Card
                                key={tool.id}
                                className="group border-0 shadow-md transition-all duration-200 hover:-translate-y-1 hover:shadow-lg"
                            >
                                <CardHeader className="pb-3">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="rounded-lg bg-gradient-to-br from-blue-100 to-purple-100 p-2 text-blue-600 transition-colors group-hover:from-blue-200 group-hover:to-purple-200">
                                                {tool.icon}
                                            </div>
                                            <div>
                                                <CardTitle className="text-lg transition-colors group-hover:text-blue-600">
                                                    {tool.name}
                                                </CardTitle>
                                                <div className="mt-1 flex items-center gap-2">
                                                    <Badge
                                                        variant="secondary"
                                                        className="text-xs"
                                                    >
                                                        {tool.category}
                                                    </Badge>
                                                    {tool.isNew && (
                                                        <Badge className="bg-green-100 text-xs text-green-700 hover:bg-green-100">
                                                            New
                                                        </Badge>
                                                    )}
                                                    {tool.isPopular && (
                                                        <Badge className="bg-orange-100 text-xs text-orange-700 hover:bg-orange-100">
                                                            Popular
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="pt-0">
                                    <CardDescription className="mb-4 line-clamp-2 text-sm">
                                        {tool.description}
                                    </CardDescription>
                                    <Button
                                        className="w-full"
                                        variant="default"
                                        onClick={() => {
                                            if (tool.id === 'api-tester') {
                                                router.visit(
                                                    route('endpoints.index'),
                                                );
                                            } else if (
                                                tool.id === 'certificate-viewer'
                                            ) {
                                                router.visit(
                                                    '/certificate-viewer',
                                                );
                                            } else if (
                                                tool.id === 'product-planning'
                                            ) {
                                                router.visit(
                                                    route(
                                                        'product-planning.index',
                                                    ),
                                                );
                                            } else if (tool.id === 'base64') {
                                                router.visit(
                                                    route('base64.index'),
                                                );
                                            } else if (
                                                tool.id ===
                                                'sample-data-generator'
                                            ) {
                                                router.visit(
                                                    route(
                                                        'sample-data-generator.index',
                                                    ),
                                                );
                                            }
                                        }}
                                    >
                                        {tool.id === 'api-tester'
                                            ? 'Open API Debugger'
                                            : `Open ${tool.name}`}
                                    </Button>
                                </CardContent>
                            </Card>
                        ))}
                    </div>

                    {filteredTools.length === 0 && (
                        <div className="py-12 text-center">
                            <div className="mb-4 text-muted-foreground">
                                <Search className="mx-auto mb-4 h-12 w-12 opacity-50" />
                                <p className="text-lg">No tools found</p>
                                <p className="text-sm">
                                    Try adjusting your search or filter criteria
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </GuestLayout>
    );
}
